import type { AnyObjectSchema } from 'yup';
import type { ZodType } from 'zod';

import { yupResolver } from '@hookform/resolvers/yup';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import {
  useForm,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import { ZodEffects, ZodObject } from 'zod';

type Schema<T> = AnyObjectSchema | ZodType<T>;

interface UseBaseFormProps<T extends FieldValues> extends Omit<UseFormProps<T>, 'resolver'> {
  schema: Schema<T>;
  validateOnChange?: boolean;
}

type ExtendedUseFormReturn<T extends FieldValues> = UseFormReturn<T> & {
  formData: Partial<T>;
};

export const useBaseForm = <T extends FieldValues>({
  schema,
  defaultValues,
  validateOnChange = true,
}: UseBaseFormProps<T>): ExtendedUseFormReturn<T> => {
  const resolver = 'fields' in schema ? yupResolver(schema) : zodResolver(schema);

  const methods = useForm<T>({
    resolver,
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { formState, trigger, watch, getValues } = methods;

  const getSchemaFields = (schema: Schema<T>): string[] => {
    if ('fields' in schema) {
      return Object.keys(schema.fields);
    }

    if (schema instanceof ZodEffects) {
      return getSchemaFields(schema.innerType());
    }

    if (schema instanceof ZodObject) {
      return Object.keys(schema.shape);
    }

    return [];
  };

  // SAFETY: the schema validates T, so its top-level field names are paths of T.
  const watchFields = getSchemaFields(schema) as Array<Path<T>>;

  watch(watchFields);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (validateOnChange && formState.isSubmitted && name) {
        void trigger(schema instanceof ZodEffects ? undefined : name);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, trigger, validateOnChange, formState.isSubmitted, schema]);

  const setValue = (
    field: Path<T>,
    value: PathValue<T, Path<T>>,
    options?: { shouldDirty?: boolean; shouldTouch?: boolean },
  ) => {
    const defaultOptions = {
      shouldDirty: true,
      shouldTouch: true,
    };

    methods.setValue(field, value, { ...defaultOptions, ...options });
  };

  return {
    ...methods,
    setValue,
    formData: getValues(),
  };
};
